import { ContentProvider } from './ContentProvider.js';
import { GENERATOR_TYPES } from './GeneratorRegistry.js';
import { ROLE, LAYER, ROLE_DEFAULT_LAYER } from './TileMeta.js';

/**
 * Content provider that uses the current editor state.
 *
 * Priority for tile mappings:
 *   1. Custom overrides (setTileMapping)
 *   2. TileMeta — user-tagged data from the Tile Tagger
 *   3. Quarter heuristic — blind division of tileset (legacy fallback)
 *   4. Generator defaults — hardcoded numeric IDs
 */
export class LocalContentProvider extends ContentProvider {
  constructor(state) {
    super();
    this.state = state;
    this._tileMappings = new Map(); // generatorType → tile mapping overrides
  }

  /** Set custom tile mappings for a generator type. */
  setTileMapping(generatorType, mapping) {
    this._tileMappings.set(generatorType, mapping);
  }

  getTiles(generatorType) {
    // Custom mapping takes priority
    const custom = this._tileMappings.get(generatorType);
    if (custom) return custom;

    // Try to build mapping from loaded tileset
    const tilesets = this.state.get('tilesets');
    if (tilesets && tilesets.length > 0) {
      const ts = tilesets[0]; // Use the first loaded tileset

      // If the tileset has TileMeta (user-tagged), use that
      if (ts.tileMeta) {
        const mapping = this._buildTilesFromMeta(ts);
        if (mapping) return mapping;
      }

      // Fallback: quarter heuristic
      const mapping = this._buildTilesFromHeuristic(ts);
      if (mapping) return mapping;
    }

    // Fall back to generator defaults
    const entry = GENERATOR_TYPES[generatorType];
    return entry?.defaultTiles || {};
  }

  getTilesets() {
    return this.state.get('tilesets').map(ts => ({
      columns: ts.columns,
      firstgid: ts.firstGid,
      image: ts.image,
      imageheight: ts.imageHeight,
      imagewidth: ts.imageWidth,
      margin: ts.margin || 0,
      name: ts.name,
      spacing: ts.spacing || 0,
      tilecount: ts.tileCount,
      tileheight: ts.tileHeight,
      tilewidth: ts.tileWidth,
    }));
  }

  getRoomTemplates(generatorType) {
    return [];
  }

  getProfile(generatorType) {
    return {};
  }

  /**
   * Build tile mapping from TileMeta (user-tagged tileset data).
   * This uses the actual semantic tags the user assigned, so the generator
   * gets the correct tiles for each role.
   */
  _buildTilesFromMeta(ts) {
    const meta = ts.tileMeta;
    const gid = ts.firstGid || 0;
    const tiles = {};

    // ── Terrain groups → generator terrain slots ──
    const terrainGroups = meta.terrainGroups || [];
    for (const tg of terrainGroups) {
      const name = tg.id.toLowerCase();
      tiles[name] = gid + tg.centerTile;
      if (tg.randomVariants && tg.randomVariants.length > 0) {
        tiles[`${name}Variants`] = tg.randomVariants.map(id => gid + id);
      }
      // Map common aliases
      if (name === 'grass' || name === 'ground') {
        tiles.grass = gid + tg.centerTile;
        tiles.grassVariants = (tg.randomVariants || []).map(id => gid + id);
      }
      if (name === 'water') {
        tiles.water = gid + tg.centerTile;
      }
      if (name === 'dirt' || name === 'earth') {
        tiles.dirt = gid + tg.centerTile;
      }
      if (name === 'path' || name === 'road') {
        tiles.path = gid + tg.centerTile;
        tiles.road = gid + tg.centerTile;
      }
      if (name === 'sand') {
        tiles.sand = gid + tg.centerTile;
      }
    }

    // ── Wall sets → generator wall slots ──
    const wallSets = meta.wallSets || [];
    if (wallSets.length > 0) {
      const ws = wallSets[0]; // primary wall set
      if (ws.top != null) tiles.wall = gid + ws.top;
      if (ws.top != null) tiles.wallTop = gid + ws.top;
      if (ws.bottom != null) tiles.wallBottom = gid + ws.bottom;
      if (ws.left != null) tiles.wallLeft = gid + ws.left;
      if (ws.right != null) tiles.wallRight = gid + ws.right;
      // Collect all wall tiles as variants
      const wallTiles = [ws.top, ws.bottom, ws.left, ws.right].filter(t => t != null);
      if (wallTiles.length > 0) {
        tiles.wallVariants = wallTiles.map(id => gid + id);
      }
    }

    // ── Floor types → generator floor slots ──
    const floorTypes = meta.floorTypes || [];
    if (floorTypes.length > 0) {
      const ft = floorTypes[0]; // primary floor
      tiles.floor = gid + ft.tile;
      if (ft.variants && ft.variants.length > 0) {
        tiles.floorVariants = ft.variants.map(id => gid + id);
      }
    }

    // ── Openings → door/window ──
    const openings = meta.openings || [];
    for (const op of openings) {
      if (op.type === 'door') tiles.door = gid + op.tile;
      if (op.type === 'window') tiles.window = gid + op.tile;
    }

    // ── Objects → named slots ──
    const objects = meta.objects || [];
    for (const obj of objects) {
      const name = obj.id.toLowerCase();
      // Use the top-left tile of the object
      const topLeft = obj.tiles?.[0]?.[0];
      if (topLeft != null) tiles[name] = gid + topLeft;
    }

    // ── Decorations → named slots ──
    const decorations = meta.decorations || [];
    const decoTiles = [];
    for (const dec of decorations) {
      const name = dec.id.toLowerCase();
      tiles[name] = gid + dec.tile;
      decoTiles.push(gid + dec.tile);
    }
    if (decoTiles.length > 0) {
      tiles.decorations = decoTiles;
    }

    // ── Role-based fallbacks — scan tileRoles for anything not yet mapped ──
    const tileRoles = meta.tileRoles || {};
    const byRole = {};
    for (const [idStr, role] of Object.entries(tileRoles)) {
      if (!byRole[role]) byRole[role] = [];
      byRole[role].push(gid + Number(idStr));
    }

    // Fill in any terrain/structure slots not covered by groups
    if (!tiles.grass && byRole[ROLE.TERRAIN]?.length > 0) {
      tiles.grass = byRole[ROLE.TERRAIN][0];
      tiles.grassVariants = byRole[ROLE.TERRAIN].slice(0, 4);
    }
    if (!tiles.wall && byRole[ROLE.WALL]?.length > 0) {
      tiles.wall = byRole[ROLE.WALL][0];
      tiles.wallVariants = byRole[ROLE.WALL].slice(0, 4);
    }
    if (!tiles.floor && byRole[ROLE.FLOOR]?.length > 0) {
      tiles.floor = byRole[ROLE.FLOOR][0];
      tiles.floorVariants = byRole[ROLE.FLOOR].slice(0, 4);
    }
    if (!tiles.tree && byRole[ROLE.OBJECT]?.length > 0) {
      tiles.tree = byRole[ROLE.OBJECT][0];
    }

    // ── Tile properties (passable/blocking) ──
    if (meta.tileProperties) {
      tiles._tileProperties = {};
      for (const [idStr, props] of Object.entries(meta.tileProperties)) {
        tiles._tileProperties[gid + Number(idStr)] = props;
      }
    }

    // ── Layer map — tileId → target layer ──
    // Priority: tileProperties.layer → structure layer field → role default
    tiles._tileLayers = {};
    if (meta.buildLayerMap) {
      // TileMeta instance — use its built-in resolver (handles all priority)
      const rawMap = meta.buildLayerMap();
      for (const [idStr, layer] of Object.entries(rawMap)) {
        tiles._tileLayers[gid + Number(idStr)] = layer;
      }
    } else {
      // Plain data object — build from tileProperties + roles
      const tileRoles = meta.tileRoles || {};
      const tileProperties = meta.tileProperties || {};
      for (const [idStr, props] of Object.entries(tileProperties)) {
        if (props.layer) {
          tiles._tileLayers[gid + Number(idStr)] = props.layer;
        }
      }
      for (const [idStr, role] of Object.entries(tileRoles)) {
        const tid = gid + Number(idStr);
        if (!tiles._tileLayers[tid]) {
          tiles._tileLayers[tid] = ROLE_DEFAULT_LAYER[role] || LAYER.OBJECTS;
        }
      }
    }

    // Only return meta-built mapping if we got at least some useful data
    const slotCount = Object.keys(tiles).filter(k => !k.startsWith('_')).length;
    if (slotCount < 2) return null;

    return tiles;
  }

  /**
   * Legacy heuristic: divide tileset into quarters.
   * Used when no TileMeta is available.
   */
  _buildTilesFromHeuristic(ts) {
    const gid = ts.firstGid;
    const count = ts.tileCount;
    if (count < 4) return null;

    const q = Math.floor(count / 4);
    const q1 = gid;
    const q2 = gid + q;
    const q3 = gid + q * 2;
    const q4 = gid + q * 3;

    const tiles = {
      grass: q1,
      grassVariants: this._range(q1, Math.min(3, q)),
      dirt: q1 + Math.min(1, q - 1),
      path: q1 + Math.min(2, q - 1),
      water: q2,
      waterEdge: q2 + 1,
      sand: q2 + Math.min(2, q - 1),
      stone: q2 + Math.min(3, q - 1),
      cobble: q2 + Math.min(4, q - 1),
      lava: q2 + Math.min(5, q - 1),
      wall: q3,
      wallTop: q3 + 1,
      wallVariants: this._range(q3, Math.min(3, q)),
      floor: q3 + Math.min(3, q - 1),
      floorVariants: this._range(q3 + 3, Math.min(3, q - 3)),
      door: q3 + Math.min(6, q - 1),
      window: q3 + Math.min(7, q - 1),
      roof: q3 + Math.min(8, q - 1),
      roofEdge: q3 + Math.min(9, q - 1),
      fence: q3 + Math.min(10, q - 1),
      tree: q4,
      treeTop: q4 + 1,
      trees: this._range(q4, Math.min(3, count - q * 3)),
      treeTops: this._range(q4 + 1, Math.min(3, count - q * 3 - 1)),
      bush: q4 + Math.min(3, count - q * 3 - 1),
      flower: q4 + Math.min(4, count - q * 3 - 1),
      rock: q4 + Math.min(5, count - q * 3 - 1),
      decorations: this._range(q4 + 3, Math.min(5, count - q * 3 - 3)),
      bed: q4 + Math.min(6, count - q * 3 - 1),
      table: q4 + Math.min(7, count - q * 3 - 1),
      chair: q4 + Math.min(8, count - q * 3 - 1),
      crate: q4 + Math.min(9, count - q * 3 - 1),
      barrel: q4 + Math.min(10, count - q * 3 - 1),
      counter: q4 + Math.min(11, count - q * 3 - 1),
      bookshelf: q4 + Math.min(12, count - q * 3 - 1),
      desk: q4 + Math.min(13, count - q * 3 - 1),
      rug: q4 + Math.min(14, count - q * 3 - 1),
      crystal: q2 + Math.min(6, q - 1),
      crystals: this._range(q2 + 6, Math.min(2, q - 6)),
      stalagmite: q2 + Math.min(8, q - 1),
      stalagmites: this._range(q2 + 8, Math.min(2, q - 8)),
      lamppost: q4 + Math.min(15, count - q * 3 - 1),
      road: q1 + Math.min(3, q - 1),
      sidewalk: q1 + Math.min(4, q - 1),
      asphalt: q1 + Math.min(5, q - 1),
    };

    return tiles;
  }

  /** Generate array of consecutive tile IDs starting at `start`, length `count`. */
  _range(start, count) {
    if (count <= 0) return [start];
    return Array.from({ length: count }, (_, i) => start + i);
  }
}
